import { CheckCircle2, ChevronDown, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Slider } from '@/components/ui/slider';
import { UserCell } from '@/components/tombolo/UserCell';
import { TRUST_RULES } from '@/lib/tombolo';
import type { TomboloState } from '@/hooks/useTombolo';
import type { TrustResult } from '@/lib/tombolo';

function ScoreBadge({ score, minScore }: { score: number; minScore: number }) {
  const ok = score >= minScore;
  return (
    <Badge
      className={ok
        ? 'bg-emerald-600 hover:bg-emerald-600 text-white tabular-nums'
        : 'bg-destructive hover:bg-destructive text-white tabular-nums'}
    >
      {score}
    </Badge>
  );
}

function TrustDetails({ trust }: { trust: TrustResult }) {
  return (
    <div className="mt-2 grid gap-1 text-xs">
      {trust.positives.map((p, i) => (
        <p key={`p-${i}`} className="flex items-start gap-1.5 text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="h-3.5 w-3.5 mt-px shrink-0" aria-hidden /> {p}
        </p>
      ))}
      {trust.negatives.map((n, i) => (
        <p key={`n-${i}`} className="flex items-start gap-1.5 text-muted-foreground">
          <XCircle className="h-3.5 w-3.5 mt-px shrink-0" aria-hidden /> {n}
        </p>
      ))}
    </div>
  );
}

export function StepFilter({ state }: { state: TomboloState }) {
  const {
    engagement, minScore, setMinScore, seedInput, setSeedInput, seedPubkeys,
    isScoring, scoringProgress, signals, runScoring,
    eligible, filteredOut, setStep, isLocked,
  } = state;

  if (!engagement) return null;

  const hasScores = signals !== null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" aria-hidden />
          Bot filter
        </CardTitle>
        <CardDescription className="text-base">
          Every person gets a simple trust score from 0 to 100 based on public
          signals like having a profile, followers and real activity.
          Nothing is hidden — you can see exactly why each person passed or was filtered out.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* How scoring works — always visible for transparency */}
        <Collapsible>
          <CollapsibleTrigger className="flex w-full items-center justify-between rounded-lg border bg-muted/40 p-4 text-sm font-medium hover:bg-muted/60 transition-colors [&[data-state=open]>svg]:rotate-180">
            How the score is calculated
            <ChevronDown className="h-4 w-4 transition-transform" aria-hidden />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <ul className="rounded-b-lg border border-t-0 p-4 text-sm space-y-1">
              {TRUST_RULES.map((rule) => (
                <li key={rule.label} className="flex justify-between gap-4">
                  <span>{rule.label}</span>
                  <span className={`font-mono tabular-nums ${rule.points < 0 ? 'text-destructive' : 'text-emerald-700 dark:text-emerald-400'}`}>
                    {rule.points > 0 ? `+${rule.points}` : rule.points}
                  </span>
                </li>
              ))}
            </ul>
          </CollapsibleContent>
        </Collapsible>

        {/* Settings */}
        <div className="space-y-4 rounded-lg border p-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="min-score" className="text-base">Minimum score to get a ticket</Label>
              <span className="text-lg font-bold tabular-nums">{minScore}</span>
            </div>
            <Slider
              id="min-score"
              value={[minScore]}
              onValueChange={([v]) => setMinScore(v)}
              min={0}
              max={100}
              step={5}
              disabled={isLocked}
              aria-label="Minimum trust score"
            />
            <p className="text-sm text-muted-foreground">
              0 lets everyone in. Higher values filter out empty and bot-like accounts more strictly.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="seeds" className="text-base">Trusted seed accounts (optional)</Label>
            <Input
              id="seeds"
              value={seedInput}
              onChange={(e) => setSeedInput(e.target.value)}
              disabled={isLocked}
              placeholder="npub1… (separate several with spaces or commas)"
              className="font-mono text-sm"
            />
            <p className="text-sm text-muted-foreground">
              People followed by these accounts get +15 bonus points.
              {seedPubkeys.length > 0 && ` ${seedPubkeys.length} seed account${seedPubkeys.length > 1 ? 's' : ''} recognized.`}
            </p>
          </div>
        </div>

        {!hasScores ? (
          <div className="flex justify-center py-4">
            <Button size="lg" onClick={runScoring} disabled={isScoring}>
              {isScoring ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden />
                  {scoringProgress || 'Checking accounts…'}
                </>
              ) : (
                `Check ${engagement.participants.length} accounts`
              )}
            </Button>
          </div>
        ) : (
          <>
            {/* Eligible */}
            <section aria-label="Eligible participants">
              <h3 className="flex items-center gap-2 font-semibold mb-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-hidden />
                Eligible ({eligible.length})
              </h3>
              {eligible.length === 0 ? (
                <p className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
                  Nobody passes with this minimum score. Try lowering it.
                </p>
              ) : (
                <ScrollArea className="h-64 rounded-lg border">
                  <ul className="divide-y">
                    {eligible.map(({ participant, trust }) => (
                      <li key={participant.pubkey} className="p-3">
                        <Collapsible>
                          <div className="flex items-center justify-between gap-3">
                            <UserCell pubkey={participant.pubkey} metadata={engagement.profiles[participant.pubkey]} />
                            <div className="flex items-center gap-2 shrink-0">
                              <ScoreBadge score={trust.score} minScore={minScore} />
                              <CollapsibleTrigger className="rounded p-1 hover:bg-muted [&[data-state=open]>svg]:rotate-180" aria-label="Show score details">
                                <ChevronDown className="h-4 w-4 transition-transform" aria-hidden />
                              </CollapsibleTrigger>
                            </div>
                          </div>
                          <CollapsibleContent>
                            <TrustDetails trust={trust} />
                          </CollapsibleContent>
                        </Collapsible>
                      </li>
                    ))}
                  </ul>
                </ScrollArea>
              )}
            </section>

            {/* Filtered out */}
            <section aria-label="Filtered out participants">
              <h3 className="flex items-center gap-2 font-semibold mb-2">
                <XCircle className="h-5 w-5 text-destructive" aria-hidden />
                Filtered out ({filteredOut.length})
              </h3>
              {filteredOut.length === 0 ? (
                <p className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
                  Nobody was filtered out with this minimum score.
                </p>
              ) : (
                <ScrollArea className="h-64 rounded-lg border">
                  <ul className="divide-y">
                    {filteredOut.map(({ participant, trust, reasons }) => (
                      <li key={participant.pubkey} className="p-3">
                        <Collapsible>
                          <div className="flex items-center justify-between gap-3">
                            <UserCell pubkey={participant.pubkey} metadata={engagement.profiles[participant.pubkey]} />
                            <div className="flex items-center gap-2 shrink-0">
                              <ScoreBadge score={trust.score} minScore={minScore} />
                              <CollapsibleTrigger className="rounded p-1 hover:bg-muted [&[data-state=open]>svg]:rotate-180" aria-label="Show filter reasons">
                                <ChevronDown className="h-4 w-4 transition-transform" aria-hidden />
                              </CollapsibleTrigger>
                            </div>
                          </div>
                          <p className="text-xs text-destructive mt-1">{reasons[0]}</p>
                          <CollapsibleContent>
                            <TrustDetails trust={trust} />
                          </CollapsibleContent>
                        </Collapsible>
                      </li>
                    ))}
                  </ul>
                </ScrollArea>
              )}
            </section>
          </>
        )}

        <div className="flex flex-col-reverse sm:flex-row justify-between gap-3">
          <Button variant="outline" onClick={() => setStep('participants')}>
            Back
          </Button>
          <Button size="lg" onClick={() => setStep('tickets')} disabled={!hasScores || eligible.length === 0}>
            Assign tickets to {eligible.length} people
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
